/**
 * ReportIssueFab — a small, neutral floating button that opens a pre-filled
 * GitHub issue for the current module. Rendered as a link (MUI Fab auto-renders
 * an anchor when `href` is set), so it's keyboard-activatable, popup-blocker-proof,
 * and opens in a new tab. Sits stacked just above the AiTutorWidget FAB.
 */
import React from "react";
import { Fab, Tooltip } from "@mui/material";
import BugReportOutlinedIcon from "@mui/icons-material/BugReportOutlined";
import { useTranslation } from "react-i18next";
import { buildReportIssueUrl } from "../utils/reportIssue";

interface ReportIssueFabProps {
  slug: string;
  moduleTitle?: string;
}

export default function ReportIssueFab({ slug, moduleTitle }: ReportIssueFabProps) {
  const { t } = useTranslation("common");
  const href = buildReportIssueUrl({
    slug,
    moduleTitle,
    pageUrl: typeof window !== "undefined" ? window.location.href : "",
  });

  return (
    <Tooltip title={t("ReportIssueFab.reportIssue")}>
      <Fab
        size="small"
        component="a"
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={t("ReportIssueFab.reportIssue")}
        sx={{
          position: "fixed",
          bottom: 88,
          right: 24,
          bgcolor: (t) => (t.palette.mode === "light" ? t.palette.grey[200] : t.palette.grey[800]),
          color: "text.secondary",
          border: "1px solid",
          borderColor: "divider",
          "&:hover": {
            bgcolor: (t) => (t.palette.mode === "light" ? t.palette.grey[300] : t.palette.grey[700]),
          },
          zIndex: (t) => t.zIndex.drawer - 1,
        }}
      >
        <BugReportOutlinedIcon fontSize="small" />
      </Fab>
    </Tooltip>
  );
}
